import { Suspense, lazy } from "react";
import { ArrowRight } from "lucide-react";
import Reveal from "./Reveal";
import LazyMount from "./LazyMount";
import { WHATSAPP_URL } from "../../lib/contact";
import { useI18n } from "../../lib/i18n";

const HeroCanvas = lazy(() => import("../three/HeroCanvas"));

export default function Hero({ settings }) {
  const { t, cmsT } = useI18n();
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden bg-bg min-h-[88vh]"
    >
      {/* 3D Canvas
          Desktop: right half (so the sphere sits on the right side of the hero).
          Mobile: lower portion (so it sits below the headline/subheadline/CTAs). */}
      <div className="absolute inset-x-0 bottom-0 top-[60%] md:inset-0 md:top-0 md:left-[28%] z-0">
        <LazyMount whenIdle rootMargin="0px">
          <Suspense fallback={null}>
            <HeroCanvas />
          </Suspense>
        </LazyMount>
      </div>

      {/* Subtle dot pattern */}
      <div className="absolute inset-0 hero-dots opacity-25 pointer-events-none z-[1]" />

      {/* Desktop: left→center fade so the sphere transitions smoothly into the text area. */}
      <div className="absolute inset-y-0 left-0 w-2/3 hidden md:block bg-gradient-to-r from-bg via-bg/85 to-transparent pointer-events-none z-[2]" />
      {/* Mobile: top fade above the canvas to soften the line between text & geometry. */}
      <div className="absolute top-[48%] inset-x-0 h-32 md:hidden bg-gradient-to-b from-bg via-bg/80 to-transparent pointer-events-none z-[2]" />
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-bg to-transparent pointer-events-none z-[2]" />
      <div className="container-x relative z-10">
        <div className="max-w-2xl">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-ember" />
              <span className="text-ember uppercase font-body font-medium text-xs tracking-[0.28em]">
                {cmsT(settings.heroEyebrow)}
              </span>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-display font-extrabold text-primary-fg uppercase leading-[0.95] text-[36px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[88px] max-w-[18ch]"
            >
              {cmsT(settings.heroHeadline)}
            </h1>
          </Reveal>

          <Reveal delay={160}>
            <p
              data-testid="hero-subheadline"
              className="mt-8 max-w-xl font-body leading-relaxed border-l-[3px] border-ember pl-3"
              style={{
                color: "#FFFFFF",
                fontSize: "18px",
                fontWeight: 400,
                whiteSpace: "pre-line",
              }}
            >
              {cmsT(settings.heroSubheadline)}
            </p>
          </Reveal>

          <Reveal delay={240}>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 h-12 px-6 rounded-md bg-ember hover:bg-ember/90 text-white font-medium tracking-wide transition-colors"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </a>
              <a
                href="#servicios"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-line hover:border-ember hover:text-ember text-primary-fg font-medium tracking-wide transition-colors backdrop-blur-sm bg-bg/40"
              >
                {t("hero.ctaSecondary")}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
